import * as React from 'react';
import './ProfessionalForm'
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import { DateInterval, ProfessionalPayment, ProfessionalPaymentType } from '../../../models';
import { hide } from '../../../shared/style';
import { getPaymentsSettings } from '../../../store/settings/selectors';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AutocompleteTextField, NumberInputField } from '../fields';
import { DateIntervalField } from '../fields/DateIntervalField';
import Add from '@material-ui/icons/Add';
import Remove from '@material-ui/icons/Remove';
import AppButton from '../../AppButton';

export interface ProfessionalPaymentProps {
    id: string,
    data: ProfessionalPayment[],
    types?: ProfessionalPaymentType[],
    onChange: (data: ProfessionalPayment[]) => void,
}

const getInputAddon = (type: ProfessionalPaymentType): string|undefined => {
    switch(type) {
        case ProfessionalPaymentType.Fixed: return '€'
        case ProfessionalPaymentType.PerHour: return '€/h'
        case ProfessionalPaymentType.Percentage: return '%'
        case ProfessionalPaymentType.PercentageEffective: return '%'
        default: return undefined
    }
}

export const ProfessionalPaymentInput: React.FC<ProfessionalPaymentProps> = ({
    id,
    data,
    types = [
        ProfessionalPaymentType.Fixed,
        ProfessionalPaymentType.PerHour,
        ProfessionalPaymentType.Percentage,
        ProfessionalPaymentType.PercentageEffective,
        ProfessionalPaymentType.Profile,
    ],
    onChange,
}) => {
    const t = useTranslations()

    const _profiles = useSelector(getPaymentsSettings)

    const profiles = useMemo(() => _profiles.filter(p => !_profiles.some(pp => pp.id === p.id && pp.version > p.version)), [_profiles])

    const typeMap = useMemo<Record<string, ProfessionalPaymentType>>(() => ({
        [t(TK.percentageAppointmentPrice)]: ProfessionalPaymentType.Percentage,
        [t(TK.percentageAmountPaid)]: ProfessionalPaymentType.PercentageEffective,
        [t(TK.perHour)]: ProfessionalPaymentType.PerHour,
        [t(TK.fixedValue)]: ProfessionalPaymentType.Fixed,
        [t(TK.paymentProfile)]: ProfessionalPaymentType.Profile,
    }), [t])

    const showValue = data.some(v => v.type && v.type !== ProfessionalPaymentType.Profile)
    const showProfile = data.some(v => v.type === ProfessionalPaymentType.Profile)

    const onChangeRow = (idx: number, value: Partial<ProfessionalPayment>) => {
        onChange(data.map((v, i) => i === idx ? {...v, ...value} : v))
    }

    const onAddNew = () => {
        onChange([...data, {
            type: ProfessionalPaymentType.Fixed,
            value: 0,
            period: {from: new Date().toISOString().substring(0, 10), to: '2999-12-31', description: ''},
        }])
    }

    const onRemoveNew = (idx: number) => () => {
        if (data.length > 1) {
            onChange(data.filter((_, i) => i !== idx))
        }
    }

    const anyOverlap = (i: number, period: DateInterval | undefined) => {
        if (!period) return false
        const pFrom = new Date(period.from)
        const pTo = new Date(period.to)
        return data.some((v, idx) => {
            if (i === idx) return false
            if (!v.period) return false
            const vFrom = new Date(v.period.from)
            const vTo = new Date(v.period.to)
            return (pFrom <= vTo) && (vFrom <= pTo)
        })
    }

    return (<Table size='sm'>
      <thead>
          <th>{t(TK.payment)}</th>
          {showValue && <th>{t(TK.value)}</th>}
          {showProfile && <th>{t(TK.profile)}</th>}
          <th>{t(TK.interval)}</th>
          <th />
      </thead>
      <tbody>
      {data.map(({type, profileId, value, period}, i) => (
          <tr key={i}>
            <td>
                <AutocompleteTextField
                    className='paymentFieldType'
                    style={{minWidth: '20rem'}}
                    id={`${id}-${i}-type-input`}
                    value={Object.entries(typeMap).find(([_, val]) => val === type)?.[0]}
                    options={Object.entries(typeMap).filter(([_, val]) => types.includes(val)).map(([key]) => key)}
                    onValueChanged={type => onChangeRow(i, {type: typeMap[type]})}
                />
            </td>
            {showValue && <td>
                <NumberInputField
                    id={`${id}-${i}-value`}
                    style={{minWidth: '8rem'}}
                    max={type === ProfessionalPaymentType.Percentage ? 100 : undefined}
                    min={0}
                    value={value}
                    append={getInputAddon(type)}
                    onChange={value => onChangeRow(i, {profileId: undefined, value})}
                />
            </td>}
            {showProfile && <td>
                <AutocompleteTextField
                    className='paymentFieldProfile'
                    style={hide(!showProfile)}
                    id={`${id}-${i}-type-profile`}
                    value={profiles.find(p => p.id === profileId)?.name ?? profileId}
                    onValueChanged={name => onChangeRow(i, {value: undefined, profileId: profiles.find(p => p.name === name)?.id})}
                    options={profiles.map(p => p.name)} />
            </td>}
            <td  style={anyOverlap(i, period) ? {background: 'pink'} : undefined}>
                <DateIntervalField
                    id={`${id}-${i}-interval`}
                    values={period ? [period] : [] }
                    onChange={intervals => onChangeRow(i, {period: intervals[0]})}
                    maxDates={1}
                    minDates={1}
                    type='date'
                    hideDescription
                />
            </td>
            <td>
                {i > 0 && <AppButton id={id ? `${id}-${i}-remove-button` : undefined} outline onClick={onRemoveNew(i)}><Remove/></AppButton>}
                <AppButton id={id ? `${id}-${i}-add-button` : undefined} outline onClick={onAddNew}><Add/></AppButton>
            </td>
          </tr>
      ))}
      </tbody>
    </Table>)
}